import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "../../../shared/components/Button";
import InfoTooltip from "../../../shared/components/InfoTooltip";
import useBodyScrollLock from "../../../shared/hooks/useBodyScrollLock";
import brandLogo from "../../../assets/images/tnp-logo-neon.png";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import {
  getEffectiveIdentityColors,
  type OrganizerVisualIdentity,
} from "../../../shared/utils/organizerVisualIdentity";
import "./ChallengeShareModal.css";

const shareTexts = siteTexts.challenges.publicDetail.shareModal;

export type ChallengeShareData = {
  title: string;
  eventType: string;
  dateLabel: string;
  location: string;
  organizer: string;
  eventIdentityMode?: "organizer_photo" | "organizer_logo" | "tnp_standard" | null;
  organizerAvatarUrl?: string | null;
  organizerLogoUrl?: string | null;
  totalSlots: string;
  availableSlots: string;
  priceLabel: string;
  categoryLabel?: string;
  shareUrl: string;
  participant?: {
    name: string;
    avatarUrl?: string | null;
    partnerName?: string | null;
    categoryLabel?: string | null;
  } | null;
};

type ChallengeShareModalProps = {
  open: boolean;
  data: ChallengeShareData;
  visualIdentity?: OrganizerVisualIdentity | null;
  onClose: () => void;
};

type SharePreview = {
  blob: Blob;
  filename: string;
  url: string;
};

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function blobFromCanvas(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
        return;
      }
      reject(new Error(shareTexts.generateError));
    }, "image/png");
  });
}

function loadShareImage(src?: string | null) {
  const safeSrc = String(src || "").trim();
  if (!safeSrc) return Promise.resolve<HTMLImageElement | null>(null);
  return new Promise<HTMLImageElement | null>((resolve) => {
    const image = new Image();
    if (!safeSrc.startsWith("data:")) image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = safeSrc;
  });
}

function initialsFromName(value?: string | null) {
  return (
    String(value || "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((chunk) => chunk[0]?.toUpperCase() || "")
      .join("") || "TNP"
  );
}

function fitFontSize({
  context,
  text,
  maxWidth,
  maxSize,
  minSize,
  weight = 900,
}: {
  context: CanvasRenderingContext2D;
  text: string;
  maxWidth: number;
  maxSize: number;
  minSize: number;
  weight?: number;
}) {
  let size = maxSize;
  while (size >= minSize) {
    context.font = `${weight} ${size}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
    if (context.measureText(text).width <= maxWidth) return size;
    size -= 2;
  }
  return minSize;
}

function drawStoryBackground({
  context,
  blue,
  pink,
}: {
  context: CanvasRenderingContext2D;
  blue: string;
  pink: string;
}) {
  const background = context.createLinearGradient(0, 0, 1080, 1920);
  background.addColorStop(0, "#020817");
  background.addColorStop(0.5, "#071833");
  background.addColorStop(1, "#130018");
  context.fillStyle = background;
  context.fillRect(0, 0, 1080, 1920);

  context.globalAlpha = 0.82;
  context.strokeStyle = blue;
  context.lineWidth = 2;
  context.beginPath();
  context.arc(940, 90, 520, 0.25, 1.9);
  context.stroke();
  context.strokeStyle = pink;
  context.beginPath();
  context.arc(850, 1700, 660, 3.55, 5.42);
  context.stroke();
  context.globalAlpha = 1;
}

function drawLogo({
  context,
  logoImage,
  blue,
  pink,
  x,
  y,
  width,
  height,
}: {
  context: CanvasRenderingContext2D;
  logoImage: HTMLImageElement | null;
  blue: string;
  pink: string;
  x: number;
  y: number;
  width: number;
  height: number;
}) {
  if (logoImage) {
    context.drawImage(logoImage, x, y, width, height);
    return;
  }
  const logoGradient = context.createLinearGradient(
    x,
    y + 18,
    x + width,
    y + height - 14,
  );
  logoGradient.addColorStop(0, blue);
  logoGradient.addColorStop(1, pink);
  context.fillStyle = logoGradient;
  context.font = `italic 900 ${Math.round(height * 0.76)}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
  context.textAlign = "left";
  context.fillText("TNP", x, y + height * 0.7);
}

function drawContainedImage({
  context,
  image,
  x,
  y,
  width,
  height,
}: {
  context: CanvasRenderingContext2D;
  image: HTMLImageElement;
  x: number;
  y: number;
  width: number;
  height: number;
}) {
  const ratio = Math.min(width / image.width, height / image.height);
  const drawWidth = image.width * ratio;
  const drawHeight = image.height * ratio;
  context.drawImage(
    image,
    x + (width - drawWidth) / 2,
    y + (height - drawHeight) / 2,
    drawWidth,
    drawHeight,
  );
}

function drawCircularAvatar({
  context,
  image,
  name,
  x,
  y,
  size,
  blue,
  pink,
  strong,
}: {
  context: CanvasRenderingContext2D;
  image: HTMLImageElement | null;
  name: string;
  x: number;
  y: number;
  size: number;
  blue: string;
  pink: string;
  strong: string;
}) {
  const ring = context.createLinearGradient(x, y, x + size, y + size);
  ring.addColorStop(0, blue);
  ring.addColorStop(1, pink);
  context.fillStyle = ring;
  context.beginPath();
  context.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
  context.fill();

  const inner = 14;
  context.save();
  context.beginPath();
  context.arc(x + size / 2, y + size / 2, size / 2 - inner, 0, Math.PI * 2);
  context.clip();
  if (image) {
    const scale = Math.max(
      (size - inner * 2) / image.width,
      (size - inner * 2) / image.height,
    );
    const drawWidth = image.width * scale;
    const drawHeight = image.height * scale;
    context.drawImage(
      image,
      x + inner + (size - inner * 2 - drawWidth) / 2,
      y + inner + (size - inner * 2 - drawHeight) / 2,
      drawWidth,
      drawHeight,
    );
  } else {
    const fallback = context.createLinearGradient(x, y, x + size, y + size);
    fallback.addColorStop(0, blue);
    fallback.addColorStop(1, pink);
    context.fillStyle = fallback;
    context.fillRect(x + inner, y + inner, size - inner * 2, size - inner * 2);
    context.fillStyle = strong;
    context.font = `900 ${Math.round(size * 0.22)}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(initialsFromName(name), x + size / 2, y + size / 2);
    context.textBaseline = "alphabetic";
  }
  context.restore();
}

function drawFitText({
  context,
  text,
  x,
  y,
  maxWidth,
  maxSize,
  minSize,
  fillStyle,
  align = "left",
  weight = 900,
}: {
  context: CanvasRenderingContext2D;
  text: string;
  x: number;
  y: number;
  maxWidth: number;
  maxSize: number;
  minSize: number;
  fillStyle: string | CanvasGradient;
  align?: CanvasTextAlign;
  weight?: number;
}) {
  fitFontSize({ context, text, maxWidth, maxSize, minSize, weight });
  context.textAlign = align;
  context.fillStyle = fillStyle;
  context.fillText(text, x, y);
}

function wrapText({
  context,
  text,
  x,
  y,
  maxWidth,
  lineHeight,
  maxLines,
}: {
  context: CanvasRenderingContext2D;
  text: string;
  x: number;
  y: number;
  maxWidth: number;
  lineHeight: number;
  maxLines: number;
}) {
  const words = String(text || "")
    .split(/\s+/)
    .filter(Boolean);
  const lines: string[] = [];
  let current = "";
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (context.measureText(candidate).width <= maxWidth) {
      current = candidate;
      return;
    }
    if (current) lines.push(current);
    current = word;
  });
  if (current) lines.push(current);
  lines.slice(0, maxLines).forEach((line, index) => {
    const suffix =
      index === maxLines - 1 && lines.length > maxLines ? "..." : "";
    context.fillText(`${line}${suffix}`, x, y + index * lineHeight);
  });
}

function drawInfoCard({
  context,
  label,
  value,
  x,
  y,
  width,
  blue,
  strong,
  soft,
}: {
  context: CanvasRenderingContext2D;
  label: string;
  value: string;
  x: number;
  y: number;
  width: number;
  blue: string;
  strong: string;
  soft: string;
}) {
  context.fillStyle = "rgba(255,255,255,0.045)";
  context.strokeStyle = "rgba(132,166,198,0.28)";
  context.lineWidth = 2;
  context.beginPath();
  context.roundRect(x, y, width, 134, 24);
  context.fill();
  context.stroke();

  context.fillStyle = blue;
  context.font =
    "800 24px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.textAlign = "left";
  context.fillText(label.toUpperCase(), x + 26, y + 42);
  context.fillStyle = strong;
  context.font =
    "900 34px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  wrapText({
    context,
    text: value,
    x: x + 26,
    y: y + 88,
    maxWidth: width - 52,
    lineHeight: 34,
    maxLines: 2,
  });
  context.fillStyle = soft;
}

async function createParticipantChallengeShareBlob(
  data: ChallengeShareData,
  visualIdentity?: OrganizerVisualIdentity | null,
) {
  const participant = data.participant;
  if (!participant)
    return createChallengeShareBlob({ ...data, participant: null }, visualIdentity);

  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1920;
  const context = canvas.getContext("2d");
  if (!context) throw new Error(siteTexts.challenges.publicDetail.canvasUnavailable);

  const styles = getComputedStyle(document.documentElement);
  const blue =
    styles.getPropertyValue("--color-brand-blue").trim() || "#2b9cdf";
  const pink =
    styles.getPropertyValue("--color-brand-pink").trim() || "#e53682";
  const identityColors = getEffectiveIdentityColors(visualIdentity);
  const primary = visualIdentity?.mode === "custom" ? identityColors.primaryColor : blue;
  const secondary =
    visualIdentity?.mode === "custom" ? identityColors.secondaryColor : pink;
  const strong =
    styles.getPropertyValue("--color-brand-white").trim() || "#ffffff";
  const soft = "rgba(222,235,245,0.84)";
  const muted = "rgba(193,214,230,0.72)";
  const logoImage = await loadShareImage(brandLogo);
  const eventLogoImage =
    data.eventIdentityMode === "organizer_logo"
      ? await loadShareImage(data.organizerLogoUrl || "")
      : null;
  const avatarImage = await loadShareImage(participant.avatarUrl || "");
  const participantName =
    String(participant.name || "").trim() || shareTexts.athleteFallback;
  const partnerName = String(participant.partnerName || "").trim();
  const categoryText =
    String(participant.categoryLabel || data.categoryLabel || "").trim() ||
    shareTexts.categoryFallback;

  drawStoryBackground({ context, blue: primary, pink: secondary });

  context.fillStyle = "rgba(255,255,255,0.04)";
  context.strokeStyle = "rgba(132,166,198,0.34)";
  context.lineWidth = 2;
  context.beginPath();
  context.roundRect(74, 86, 932, 1660, 36);
  context.fill();
  context.stroke();

  if (eventLogoImage) {
    drawContainedImage({
      context,
      image: eventLogoImage,
      x: 112,
      y: 126,
      width: 130,
      height: 92,
    });
  } else {
    drawLogo({
      context,
      logoImage,
      blue: primary,
      pink: secondary,
      x: 112,
      y: 126,
      width: 130,
      height: 92,
    });
  }

  const typeGradient = context.createLinearGradient(678, 134, 952, 134);
  typeGradient.addColorStop(0, primary);
  typeGradient.addColorStop(1, secondary);
  context.fillStyle = typeGradient;
  context.beginPath();
  context.roundRect(668, 124, 300, 62, 20);
  context.fill();
  drawFitText({
    context,
    text: data.eventType.toUpperCase(),
    x: 818,
    y: 165,
    maxWidth: 244,
    maxSize: 27,
    minSize: 19,
    fillStyle: strong,
    align: "center",
  });

  context.textAlign = "center";
  const headlineGradient = context.createLinearGradient(190, 292, 890, 292);
  headlineGradient.addColorStop(0, primary);
  headlineGradient.addColorStop(0.48, strong);
  headlineGradient.addColorStop(1, secondary);
  context.fillStyle = headlineGradient;
  context.font =
    "900 82px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  wrapText({
    context,
    text: shareTexts.participating,
    x: 540,
    y: 318,
    maxWidth: 820,
    lineHeight: 84,
    maxLines: 2,
  });

  context.fillStyle = soft;
  context.font =
    "800 34px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.fillText(shareTexts.titleGoal, 540, 452);

  drawCircularAvatar({
    context,
    image: avatarImage,
    name: participantName,
    x: 368,
    y: 520,
    size: 344,
    blue: primary,
    pink: secondary,
    strong,
  });

  context.fillStyle = strong;
  context.font =
    "900 48px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.textAlign = "center";
  wrapText({
    context,
    text: participantName,
    x: 540,
    y: 925,
    maxWidth: 760,
    lineHeight: 54,
    maxLines: 2,
  });

  if (partnerName) {
    context.fillStyle = primary;
    context.font =
      "800 29px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
    wrapText({
      context,
      text: `${shareTexts.alongsidePrefix} ${partnerName}`,
      x: 540,
      y: 1042,
      maxWidth: 760,
      lineHeight: 36,
      maxLines: 2,
    });
  }

  context.fillStyle = "rgba(255,255,255,0.05)";
  context.strokeStyle = "rgba(132,166,198,0.32)";
  context.beginPath();
  context.roundRect(112, 1148, 856, 292, 28);
  context.fill();
  context.stroke();

  context.fillStyle = secondary;
  context.font =
    "800 24px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.textAlign = "left";
  context.fillText(shareTexts.championship, 146, 1196);
  context.fillStyle = strong;
  context.font =
    "900 40px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  wrapText({
    context,
    text: data.title,
    x: 146,
    y: 1248,
    maxWidth: 788,
    lineHeight: 44,
    maxLines: 2,
  });

  const chipGradient = context.createLinearGradient(146, 1342, 514, 1342);
  chipGradient.addColorStop(0, primary);
  chipGradient.addColorStop(1, secondary);
  context.fillStyle = chipGradient;
  context.beginPath();
  context.roundRect(146, 1356, 378, 54, 18);
  context.fill();
  drawFitText({
    context,
    text: categoryText,
    x: 335,
    y: 1392,
    maxWidth: 318,
    maxSize: 24,
    minSize: 18,
    fillStyle: strong,
    align: "center",
    weight: 900,
  });

  const infoTop = 1490;
  const columnWidth = 408;
  drawInfoCard({
    context,
    label: shareTexts.date,
    value: data.dateLabel,
    x: 112,
    y: infoTop,
    width: columnWidth,
    blue: primary,
    strong,
    soft,
  });
  drawInfoCard({
    context,
    label: shareTexts.location,
    value: data.location,
    x: 560,
    y: infoTop,
    width: columnWidth,
    blue: primary,
    strong,
    soft,
  });

  context.fillStyle = muted;
  context.font =
    "800 24px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.textAlign = "center";
  context.fillText(
    `${data.availableSlots} ${shareTexts.availableSuffix} · ${data.priceLabel} ${shareTexts.perPlayerSuffix}`,
    540,
    1686,
  );

  context.fillStyle = strong;
  context.font =
    "900 42px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.fillText(shareTexts.playToo, 540, 1770);
  context.fillStyle = soft;
  context.font =
    "700 27px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.fillText(shareTexts.secureSpot, 540, 1818);
  context.fillStyle = primary;
  context.font =
    "800 28px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.fillText("todosnopadel.com.br", 540, 1872);

  return blobFromCanvas(canvas);
}

async function createChallengeShareBlob(
  data: ChallengeShareData,
  visualIdentity?: OrganizerVisualIdentity | null,
) {
  if (data.participant?.name) {
    return createParticipantChallengeShareBlob(data, visualIdentity);
  }

  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1920;
  const context = canvas.getContext("2d");
  if (!context) throw new Error(siteTexts.challenges.publicDetail.canvasUnavailable);

  const styles = getComputedStyle(document.documentElement);
  const blue =
    styles.getPropertyValue("--color-brand-blue").trim() || "#2b9cdf";
  const pink =
    styles.getPropertyValue("--color-brand-pink").trim() || "#e53682";
  const identityColors = getEffectiveIdentityColors(visualIdentity);
  const primary = visualIdentity?.mode === "custom" ? identityColors.primaryColor : blue;
  const secondary =
    visualIdentity?.mode === "custom" ? identityColors.secondaryColor : pink;
  const strong =
    styles.getPropertyValue("--color-brand-white").trim() || "#ffffff";
  const soft = "rgba(222,235,245,0.84)";
  const logoImage = await loadShareImage(brandLogo);
  const eventLogoImage =
    data.eventIdentityMode === "organizer_logo"
      ? await loadShareImage(data.organizerLogoUrl || "")
      : null;

  drawStoryBackground({ context, blue: primary, pink: secondary });

  context.fillStyle = "rgba(255,255,255,0.035)";
  context.strokeStyle = "rgba(132,166,198,0.34)";
  context.lineWidth = 2;
  context.beginPath();
  context.roundRect(74, 92, 932, 1540, 32);
  context.fill();
  context.stroke();

  if (eventLogoImage) {
    drawContainedImage({
      context,
      image: eventLogoImage,
      x: 112,
      y: 132,
      width: 142,
      height: 100,
    });
  } else {
    drawLogo({
      context,
      logoImage,
      blue: primary,
      pink: secondary,
      x: 112,
      y: 132,
      width: 142,
      height: 100,
    });
  }

  const typeGradient = context.createLinearGradient(696, 146, 950, 146);
  typeGradient.addColorStop(0, primary);
  typeGradient.addColorStop(1, secondary);
  context.fillStyle = typeGradient;
  context.beginPath();
  context.roundRect(682, 132, 284, 62, 20);
  context.fill();
  drawFitText({
    context,
    text: data.eventType.toUpperCase(),
    x: 824,
    y: 173,
    maxWidth: 226,
    maxSize: 27,
    minSize: 20,
    fillStyle: strong,
    align: "center",
  });

  context.fillStyle = soft;
  context.font =
    "800 25px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.textAlign = "left";
  context.fillText(shareTexts.tnpChampionship, 112, 308);

  const titleGradient = context.createLinearGradient(112, 370, 932, 370);
  titleGradient.addColorStop(0, strong);
  titleGradient.addColorStop(0.72, strong);
  titleGradient.addColorStop(1, secondary);
  context.fillStyle = titleGradient;
  context.font =
    "900 74px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  wrapText({
    context,
    text: data.title,
    x: 112,
    y: 390,
    maxWidth: 840,
    lineHeight: 78,
    maxLines: 3,
  });

  context.fillStyle = primary;
  context.globalAlpha = 0.12;
  context.strokeStyle = primary;
  context.beginPath();
  context.roundRect(112, 650, 836, 144, 24);
  context.fill();
  context.globalAlpha = 0.42;
  context.stroke();
  context.globalAlpha = 1;
  context.fillStyle = primary;
  context.font =
    "800 24px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.fillText(shareTexts.dateTime, 144, 704);
  context.fillStyle = strong;
  context.font =
    "900 42px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  wrapText({
    context,
    text: data.dateLabel,
    x: 144,
    y: 756,
    maxWidth: 760,
    lineHeight: 42,
    maxLines: 1,
  });

  drawInfoCard({
    context,
    label: shareTexts.location,
    value: data.location,
    x: 112,
    y: 840,
    width: 404,
    blue: primary,
    strong,
    soft,
  });
  drawInfoCard({
    context,
    label: shareTexts.organization,
    value: data.organizer,
    x: 544,
    y: 840,
    width: 404,
    blue: primary,
    strong,
    soft,
  });
  drawInfoCard({
    context,
    label: shareTexts.totalSlots,
    value: data.totalSlots,
    x: 112,
    y: 1010,
    width: 404,
    blue: primary,
    strong,
    soft,
  });
  drawInfoCard({
    context,
    label: shareTexts.availableSlots,
    value: data.availableSlots,
    x: 544,
    y: 1010,
    width: 404,
    blue: primary,
    strong,
    soft,
  });
  drawInfoCard({
    context,
    label: shareTexts.valuePerPlayer,
    value: data.priceLabel,
    x: 112,
    y: 1180,
    width: data.categoryLabel ? 404 : 836,
    blue: primary,
    strong,
    soft,
  });
  if (data.categoryLabel) {
    drawInfoCard({
      context,
      label: shareTexts.category,
      value: data.categoryLabel,
      x: 544,
      y: 1180,
      width: 404,
      blue: primary,
      strong,
      soft,
    });
  }

  context.strokeStyle = secondary;
  context.globalAlpha = 0.52;
  context.lineWidth = 2;
  context.beginPath();
  context.moveTo(112, 1386);
  context.lineTo(948, 1386);
  context.stroke();
  context.globalAlpha = 1;

  context.fillStyle = strong;
  context.font =
    "900 36px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.textAlign = "center";
  context.fillText(shareTexts.pairCta, 540, 1460);
  context.fillStyle = primary;
  context.font =
    "800 28px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.fillText("todosnopadel.com.br", 540, 1530);

  return blobFromCanvas(canvas);
}

function filenameFromTitle(title: string, participant?: boolean) {
  const slug =
    String(title || "campeonato-tnp")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "campeonato-tnp";
  return `${participant ? "estou-participando-" : ""}${slug}.png`;
}

export default function ChallengeShareModal({
  open,
  data,
  visualIdentity,
  onClose,
}: ChallengeShareModalProps) {
  const [preview, setPreview] = useState<SharePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const info = siteTexts.common.info;

  const filename = useMemo(
    () => filenameFromTitle(data.title, Boolean(data.participant?.name)),
    [data.participant?.name, data.title],
  );

  const createPreview = useCallback(async () => {
    setLoading(true);
    setStatus("");
    try {
      const blob = await createChallengeShareBlob(data, visualIdentity);
      setPreview((current) => {
        if (current?.url) URL.revokeObjectURL(current.url);
        return { blob, filename, url: URL.createObjectURL(blob) };
      });
    } catch (_error) {
      setStatus(shareTexts.generateError);
    } finally {
      setLoading(false);
    }
  }, [data, filename, visualIdentity]);

  useEffect(() => {
    if (!open) return;
    void createPreview();
  }, [createPreview, open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, open]);

  useEffect(() => {
    if (open) return;
    setPreview((current) => {
      if (current?.url) URL.revokeObjectURL(current.url);
      return null;
    });
    setStatus("");
  }, [open]);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(data.shareUrl);
      setStatus(shareTexts.copied);
    } catch (_error) {
      setStatus(shareTexts.copyError);
    }
  }, [data.shareUrl]);

  const shareNative = useCallback(
    async (target: "whatsapp" | "instagram" | "stories" | "more") => {
      const text = `${data.title} - ${data.shareUrl}`;
      const file =
        preview &&
        new File([preview.blob], preview.filename, { type: "image/png" });
      const payload = file
        ? { files: [file], title: data.title, text }
        : { title: data.title, text, url: data.shareUrl };

      if (navigator.share && (!file || navigator.canShare?.(payload))) {
        await navigator.share(payload);
        return;
      }

      if (target === "whatsapp") {
        window.open(
          `https://wa.me/?text=${encodeURIComponent(text)}`,
          "_blank",
          "noopener,noreferrer",
        );
        return;
      }

      await copyLink();
      if (target === "instagram" || target === "stories") {
        setStatus(shareTexts.instagramHint);
      }
    },
    [copyLink, data.shareUrl, data.title, preview],
  );

  const downloadPreview = useCallback(() => {
    if (!preview) return;
    downloadBlob(preview.blob, preview.filename);
  }, [preview]);

  useBodyScrollLock(open);

  if (!open) return null;

  return (
    <div
      className="challenge-share-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={siteTexts.challenges.shareChampionship.title}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="challenge-share-modal">
        <div className="challenge-share-head">
          <div>
            <strong className="info-label">
              {siteTexts.challenges.shareChampionship.title}
              <InfoTooltip content={info.shareChampionship} />
            </strong>
            <span>{siteTexts.challenges.shareChampionship.description}</span>
          </div>
          <button
            type="button"
            className="challenge-share-close"
            onClick={onClose}
            aria-label={siteTexts.challenges.shareChampionship.closeAria}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              close
            </span>
          </button>
        </div>

        <div className="challenge-share-content">
          <div className="challenge-share-preview">
            {preview ? (
              <img src={preview.url} alt={shareTexts.previewAlt} />
            ) : (
              <div className="challenge-share-loading">
                {loading ? shareTexts.generating : shareTexts.unavailable}
              </div>
            )}
          </div>

          <div className="challenge-share-side">
            <div className="challenge-share-actions">
              <Button
                type="button"
                variant="pill"
                onClick={() => void shareNative("whatsapp")}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  forum
                </span>
                WhatsApp
              </Button>
              <Button
                type="button"
                variant="pill"
                onClick={() => void shareNative("instagram")}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  photo_camera
                </span>
                Instagram
              </Button>
              <Button
                type="button"
                variant="pill"
                onClick={() => void shareNative("stories")}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  auto_stories
                </span>
                Stories
              </Button>
              <Button type="button" variant="pill" onClick={copyLink}>
                <span className="material-symbols-outlined" aria-hidden="true">
                  content_copy
                </span>
                {shareTexts.copyLink}
              </Button>
              <InfoTooltip content={info.copyLink} />
              <Button
                type="button"
                variant="pill"
                onClick={() => void shareNative("more")}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  ios_share
                </span>
                {shareTexts.share}
              </Button>
            </div>

            <label className="challenge-share-link-field">
              <span className="info-label">
                {shareTexts.linkLabel}
                <InfoTooltip content={info.copyLink} />
              </span>
              <input value={data.shareUrl} readOnly />
            </label>

            <div className="challenge-share-footer-actions">
              <Button type="button" variant="pill" onClick={copyLink}>
                {shareTexts.copy}
              </Button>
              <InfoTooltip content={info.copyLink} />
              <Button
                type="button"
                variant="primary"
                onClick={downloadPreview}
                disabled={!preview}
              >
                {shareTexts.downloadPng}
              </Button>
              <InfoTooltip content={info.downloadPng} />
            </div>
            {status && <p className="challenge-share-status">{status}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
